import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './lib/AuthContext';
import { AdminAuthProvider } from './lib/AdminAuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ProfileSetup from './pages/ProfileSetup';
import Discover from './pages/Discover';
import Matches from './pages/Matches';
import Chat from './pages/Chat';
import Profile from './pages/Profile';
import Businesses from './pages/Businesses';
import BusinessDetail from './pages/BusinessDetail';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Communities from './pages/Communities';
import CommunityDetail from './pages/CommunityDetail';
import Saved from './pages/Saved';
import Submit from './pages/Submit';
import SubmitBusiness from './pages/SubmitBusiness';
import SubmitEvent from './pages/SubmitEvent';
import SubmitCommunity from './pages/SubmitCommunity';
import MySubmissions from './pages/MySubmissions';
import Preferences from './pages/Preferences';
import Settings from './pages/Settings';
import Premium from './pages/Premium';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSubmissions from './pages/admin/AdminSubmissions';
import AdminReports from './pages/admin/AdminReports';
import AdminSecurity from './pages/admin/AdminSecurity';
import AuthCallback from './pages/AuthCallback';
import UserView from './pages/UserView';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import CommunityGuidelines from './pages/legal/CommunityGuidelines';
import Safety from './pages/legal/Safety';
import './App.css';

export default function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors closeButton />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/app/login" element={<Login />} />
            <Route path="/app/signup" element={<Signup />} />
            <Route path="/app/verify" element={<VerifyEmail />} />
            <Route path="/app/forgot-password" element={<ForgotPassword />} />

            {/* OAuth callback (catches #session_id= from Emergent Google Auth) */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Legal (public) */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/safety" element={<Safety />} />

            {/* Protected — profile setup (no nav shell) */}
            <Route path="/app/setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />

            {/* Protected — main app shell */}
            <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route path="/app/discover" element={<Discover />} />
              <Route path="/app/matches" element={<Matches />} />
              <Route path="/app/chat/:matchId" element={<Chat />} />
              <Route path="/app/user/:userId" element={<UserView />} />
              <Route path="/app/profile" element={<Profile />} />
              <Route path="/app/businesses" element={<Businesses />} />
              <Route path="/app/businesses/:businessId" element={<BusinessDetail />} />
              <Route path="/app/events" element={<Events />} />
              <Route path="/app/events/:eventId" element={<EventDetail />} />
              <Route path="/app/communities" element={<Communities />} />
              <Route path="/app/communities/:communityId" element={<CommunityDetail />} />
              <Route path="/app/saved" element={<Saved />} />
              <Route path="/app/submit" element={<Submit />} />
              <Route path="/app/submit/business" element={<SubmitBusiness />} />
              <Route path="/app/submit/event" element={<SubmitEvent />} />
              <Route path="/app/submit/community" element={<SubmitCommunity />} />
              <Route path="/app/my-submissions" element={<MySubmissions />} />
              <Route path="/app/preferences" element={<Preferences />} />
              <Route path="/app/settings" element={<Settings />} />
              <Route path="/app/premium" element={<Premium />} />
            </Route>

            {/* Admin Console */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="submissions" element={<AdminSubmissions />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="security" element={<AdminSecurity />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AdminAuthProvider>
    </AuthProvider>
  );
}
